// StockHistoryMobile.jsx — onglet « Historique Produits » (mobile).
// Seul écran accessible sans magasin sélectionné (historique global entreprise).
// Réutilise EVENT_OPTIONS / extractBeforeAfterPairs du desktop.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import {
  Summary, SearchBar, Chips, EmptyState, Sheet, DetailRow, Note,
  Field, SelectInput, Pill, Mono, Ico, fmt,
} from "../produits/productMobileKit";
import { EVENT_OPTIONS, extractBeforeAfterPairs } from "../../gestion-stock/StockProductHistory";
import { fetchLifecycle, fetchWarehouses } from "./stockMobileApi";
import { eventTone, fmtDateTime, fmtQty, fmtSigned } from "./stockMobileShared";

const PAGE = 20;

// Raccourcis de la barre de puces → sélection de types côté serveur.
const QUICK = [
  { key: "all", label: "Tous", types: [] },
  { key: "sales", label: "Ventes", types: ["sales", "ecommerce_sale"] },
  { key: "purchase", label: "Achats", types: ["purchase"] },
  { key: "adjustment", label: "Ajustements", types: ["adjustment"] },
  { key: "transfer", label: "Transferts", types: ["transfer_in", "transfer_out"] },
];
const sameTypes = (a, b) => a.length === b.length && a.every((t) => b.includes(t));
const labelOf = (type) => EVENT_OPTIONS.find((o) => o.value === type)?.label || type;

export default function StockHistoryMobile({ selectedWarehouse, selectedCompany }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState(selectedWarehouse || null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => { setWarehouseFilter(selectedWarehouse || null); }, [selectedWarehouse, selectedCompany]);

  // Recherche serveur, débouncée (patron ProductionProcessMobile.jsx:131-134).
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    if (!selectedCompany) { setWarehouses([]); return; }
    let alive = true;
    fetchWarehouses(selectedCompany)
      .then((w) => alive && setWarehouses(w))
      .catch(() => alive && setWarehouses([]));
    return () => { alive = false; };
  }, [selectedCompany]);

  const load = useCallback(
    async (p) => {
      if (!selectedCompany) { setRows([]); setTotal(0); return; }
      setLoading(true);
      try {
        const { rows: incoming, total: t } = await fetchLifecycle({
          companyId: selectedCompany, page: p, limit: PAGE,
          search: appliedSearch, warehouseId: warehouseFilter,
          eventTypes, startDate, endDate,
        });
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(t);
      } catch {
        message.error("Impossible de charger l'historique produits");
        setRows([]); setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [selectedCompany, appliedSearch, warehouseFilter, eventTypes, startDate, endDate]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  const quickKey = useMemo(
    () => QUICK.find((q) => sameTypes(q.types, eventTypes))?.key ?? null,
    [eventTypes]
  );
  const onQuick = (key) => setEventTypes(QUICK.find((q) => q.key === key)?.types ?? []);
  const toggleType = (type) =>
    setEventTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const resetFilters = () => {
    setEventTypes([]); setWarehouseFilter(selectedWarehouse || null);
    setStartDate(""); setEndDate(""); setSearch("");
  };

  // Le magasin ne compte comme filtre que s'il diffère de celui du contexte.
  const warehouseOverridden =
    Boolean(warehouseFilter) && String(warehouseFilter) !== String(selectedWarehouse ?? "");
  const activeFilters =
    (eventTypes.length ? 1 : 0) + (startDate || endDate ? 1 : 0) + (warehouseOverridden ? 1 : 0);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };

  const pairs = useMemo(() => extractBeforeAfterPairs(detail?.details), [detail]);

  if (!selectedCompany) {
    return (
      <div className="body">
        <EmptyState icon="store" title="Aucune entreprise sélectionnée" text="Choisissez une entreprise pour consulter l'historique." />
      </div>
    );
  }

  return (
    <>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Événements", value: fmt(total) },
          { label: "Chargés", value: fmt(rows.length) },
        ]} />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un produit…" />
          </div>
          <button
            className="iconbtn" aria-label={`Filtres${activeFilters ? ` (${activeFilters} actifs)` : ""}`}
            aria-pressed={activeFilters > 0}
            style={activeFilters ? { background: "var(--blue)", color: "#fff" } : undefined}
            onClick={() => setFiltersOpen(true)}
          >
            <Ico name="sliders-horizontal" />
          </button>
        </div>
        <Chips value={quickKey} onChange={onQuick} options={QUICK.map((q) => ({ key: q.key, label: q.label }))} />
      </div>

      <div className="body">
        {!selectedWarehouse ? (
          <div style={{ padding: "12px 14px 0" }}>
            <Note>Aucun établissement sélectionné : l'historique affiché couvre toute l'entreprise.</Note>
          </div>
        ) : null}

        {loading && !rows.length ? (
          <EmptyState loading title="Chargement de l'historique" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="scroll-text" title="Aucun événement"
            text={appliedSearch || activeFilters
              ? "Aucun événement ne correspond à vos filtres."
              : "Les mouvements de vos produits apparaîtront ici."}
          />
        ) : (
          <div className="list">
            {rows.map((it, i) => {
              const qty = Number(it.quantity);
              const hasQty = Number.isFinite(qty) && qty !== 0;
              return (
                <button key={it.id ?? i} className="rcard" onClick={() => setDetail(it)}>
                  <Mono name={it.product_name || "?"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{it.product_name}</div>
                    <div className="rcard__meta">
                      <Pill tone={eventTone(it.event_type)} label={it.event_label || labelOf(it.event_type)} />
                      {it.item_code ? <span className="rcard__sub">{it.item_code}</span> : null}
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDateTime(it.event_date)}</span>
                      {it.warehouse_name ? <span className="rcard__sub">· {it.warehouse_name}</span> : null}
                    </div>
                  </div>
                  {hasQty ? (
                    <span className="rcard__price" style={{ color: qty > 0 ? "var(--green)" : "var(--red)" }}>
                      {fmtSigned(qty)}
                      <small> {it.unit_name || ""}</small>
                    </span>
                  ) : <span className="rcard__chev"><Ico name="chevron-right" /></span>}
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* feuille : filtres avancés */}
      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres" subtitle="Affinez l'historique affiché.">
        <Field label="Établissement">
          <SelectInput
            value={warehouseFilter ?? ""}
            onChange={(v) => setWarehouseFilter(v)}
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            placeholder="Tous les établissements"
          />
        </Field>

        <Field label="Période">
          <div style={{ display: "flex", gap: 10 }}>
            <div className="ctrl" style={{ flex: 1 }}>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="ctrl" style={{ flex: 1 }}>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </Field>

        <Field label="Types d'événement" hint="(plusieurs possibles)">
          <div className="chips" style={{ flexWrap: "wrap", overflowX: "visible" }}>
            {EVENT_OPTIONS.map((o) => (
              <button
                key={o.value}
                className={"chip" + (eventTypes.includes(o.value) ? " on" : "")}
                onClick={() => toggleType(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={resetFilters}>Réinitialiser</button>
          <button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>
            <Ico name="check" />Appliquer
          </button>
        </div>
      </Sheet>

      {/* feuille : détail d'un événement */}
      <Sheet
        open={Boolean(detail)} onClose={() => setDetail(null)}
        title={detail?.product_name} subtitle={detail?.event_label || labelOf(detail?.event_type)}
      >
        {detail ? (
          <>
            {detail.summary ? <Note>{detail.summary}</Note> : null}
            <DetailRow label="Date" value={fmtDateTime(detail.event_date)} />
            {detail.item_code ? <DetailRow label="Code article" value={detail.item_code} /> : null}
            <DetailRow label="Quantité" value={`${fmtSigned(detail.quantity)} ${detail.unit_name || ""}`.trim()} />
            {detail.opening_stock != null ? <DetailRow label="Stock initial" value={fmtQty(detail.opening_stock)} /> : null}
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            {detail.related_warehouse_name ? <DetailRow label="Établissement lié" value={detail.related_warehouse_name} /> : null}
            <DetailRow label="Utilisateur" value={detail.actor_name || "—"} />
            {detail.reference_label ? <DetailRow label="Référence" value={detail.reference_label} /> : null}
            {detail.remarks ? <DetailRow label="Remarques" value={detail.remarks} /> : null}

            {pairs.length ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Modifications</div>
                {pairs.map((p) => (
                  <DetailRow
                    key={p.key}
                    label={p.field}
                    value={`${p.before ?? "—"} → ${p.after ?? "—"}`}
                  />
                ))}
              </>
            ) : null}

            {detail.details ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Données brutes</div>
                <pre style={{
                  margin: 0, padding: 12, borderRadius: 12, background: "var(--canvas)",
                  fontSize: 12, lineHeight: 1.5, overflowX: "auto", maxHeight: 220,
                }}>
                  {JSON.stringify(detail.details, null, 2)}
                </pre>
              </>
            ) : null}
          </>
        ) : null}
      </Sheet>
    </>
  );
}
